import { useCallback, useEffect, useMemo, useState } from "react";
import { getFormStructure } from "../services/restAPI/FormsV3";

type AnyField = Record<string, any>;
type Column = { techName: string; label: string; options?: any; meta?: any };

const isHiddenField = (field: AnyField) =>
   field?.decorators?.some(
      (decorator: any) =>
         decorator.name === "HideFieldDecorator" && decorator.properties?.hide === true,
   );

export const useListTableRowForm = ({
   presenterFormId,
   apiColumns,
}: {
   presenterFormId: string;
   apiColumns: Column[];
}) => {
   const [formFields, setFormFields] = useState<AnyField[]>([]);
   const [loading, setLoading] = useState<boolean>(false);

   useEffect(() => {
      if (!presenterFormId) return;

      setLoading(true);
      getFormStructure(presenterFormId)
         .then((res) => {
            const fields = res?.data?.fields ?? [];
            setFormFields(fields);
         })
         .finally(() => setLoading(false));
   }, [presenterFormId]);

   const fieldDefById = useMemo(() => {
      const map: Record<string, AnyField> = {};
      for (const field of formFields) {
         if (!field?.id) continue;
         if (isHiddenField(field)) continue;
         map[String(field.id)] = field;
      }
      return map;
   }, [formFields]);

   const visibleColumns = useMemo(() => {
      return (apiColumns ?? [])
         .filter((column) => column?.options?.visible !== false)
         .filter((column) => {
            const field = fieldDefById[String(column.techName)];
            return field ? !isHiddenField(field) : true;
         });
   }, [apiColumns, fieldDefById]);

   const getRowFormGUID = useCallback((baseFormGUID: string, rowId: string | number) => {
      return `${baseFormGUID}::listRow::${String(rowId)}`;
   }, []);

   const isOptionObject = (value: any) =>
      value &&
      typeof value === "object" &&
      ("value" in value ||
         "originalValue" in value ||
         "decoratedValue" in value ||
         "label" in value);

   const buildFieldWithValue = useCallback((fieldDef: AnyField, rawRowValue: any) => {
      const type = fieldDef?.type;

      const field = { ...fieldDef };

      if (type === "MultiSelect") {
         field.value = Array.isArray(rawRowValue) ? rawRowValue : rawRowValue ? [rawRowValue] : [];
         return field;
      }

      if (type === "Select") {
         const value = rawRowValue;
         if (isOptionObject(value)) {
            field.value = value
               ? { originalValue: value.value ?? "", decoratedValue: value.label ?? "" }
               : { originalValue: "", decoratedValue: "" };
            return field;
         } else {
            field.value = {
               originalValue: value ?? "",
               decoratedValue: value ?? "",
            };
            return field;
         }
      }

      if (type === "Boolean") {
         field.value = { originalValue: rawRowValue === true };
         return field;
      }

      if (type === "LocalDate" || type === "LocalDateTime") {
         field.value = { originalValue: rawRowValue ?? "" };
         return field;
      }

      field.value = { originalValue: rawRowValue ?? "", decoratedValue: rawRowValue ?? "" };
      return field;
   }, []);

   return {
      loading,
      formFields,
      fieldDefById,
      visibleColumns,
      getRowFormGUID,
      buildFieldWithValue,
   };
};
